// src/Component/api/tokenStore.ts
//
// In-memory token store mirrored to localStorage.
//
// AuthContext is the lifecycle owner — it calls setTokens on login / OTP
// verify and clearTokens on logout. apiFetch reads from here directly, so
// non-React code (services, utils, plain hook files) gets auth automatically
// without depending on React context.

const ACCESS_TOKEN_KEY = "authToken";
const REFRESH_TOKEN_KEY = "refreshToken";

let accessTokenMemory: string | null = null;
let refreshTokenMemory: string | null = null;

// Hydrate from localStorage on module load so a hard refresh keeps the user
// authenticated before AuthContext has even mounted.
try {
  accessTokenMemory = localStorage.getItem(ACCESS_TOKEN_KEY);
  refreshTokenMemory = localStorage.getItem(REFRESH_TOKEN_KEY);
} catch {
  // localStorage unavailable (private mode, embedded webview) — fall through to in-memory only.
}

export const getAccessToken = (): string | null => accessTokenMemory;
export const getRefreshToken = (): string | null => refreshTokenMemory;

// `refreshToken` is optional: some refresh responses return only a new access
// token. Passing `undefined` leaves the existing refresh token untouched;
// passing `null` actively clears it.
export const setTokens = (
  accessToken: string | null,
  refreshToken?: string | null
): void => {
  accessTokenMemory = accessToken;
  if (accessToken) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  } else {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
  }
  if (refreshToken !== undefined) {
    refreshTokenMemory = refreshToken;
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    } else {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  }
};

export const clearTokens = (): void => {
  accessTokenMemory = null;
  refreshTokenMemory = null;
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};
